// eslint-disable-next-line import/no-unresolved
import Tracker from '@openreplay/tracker';

const TAG = '[OpenReplay]';

let tracker: Tracker | null = null;

/** Masked so the log line is safe to paste anywhere. */
function shortKey(value: string): string {
  if (!value) return '(empty)';
  if (value.includes('<([')) return '(unsubstituted placeholder)';
  return `${value.slice(0, 4)}***${value.slice(-4)}`;
}

function isLocalHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.localhost')
  );
}

/** Why initTracker must not run, or null when it should. */
export function getRuntimeBlockReason(): string | null {
  if (process.env.NODE_ENV !== 'production') {
    return `NODE_ENV is "${process.env.NODE_ENV}", not "production"`;
  }
  if (typeof window === 'undefined') return 'window is undefined';
  const host = window.location.hostname.toLowerCase();
  if (isLocalHost(host))
    return `hostname "${window.location.hostname}" is local`;
  return null;
}

/** Tracking runs only for production builds served from a real host. */
export function isProductionRuntime(): boolean {
  return getRuntimeBlockReason() === null;
}

/** Turns the raw start() failure reason into something actionable. */
function explainFailure(reason: string): string {
  if (reason.startsWith('Server error')) {
    return 'ingest server rejected /v1/web/start - projectKey or ingestPoint is wrong';
  }
  if (reason.startsWith('Incorrect server response')) {
    return 'ingestPoint replied, but not with an OpenReplay payload - ingestPoint is wrong';
  }
  if (/browser|doNotTrack/i.test(reason)) {
    return 'browser api missing or doNotTrack is enabled';
  }
  return reason;
}

/** start() resolves with a status object instead of throwing. */
function reportStart(outcome: unknown): void {
  const result = outcome as { success?: boolean; reason?: string } | undefined;

  if (result && result.success === false) {
    console.error(
      TAG,
      'START FAILED - not recording:',
      explainFailure(result.reason || 'unknown'),
    );
    return;
  }

  const sessionId = tracker ? tracker.getSessionID() : null;
  console.log(TAG, sessionId ? 'RECORDING' : 'start gave no session id', {
    sessionId,
  });
}

function isSet(value: string): boolean {
  return Boolean(value && value.trim()) && !value.includes('<([');
}

export function isConfigured(config: {
  projectKey: string;
  ingestPoint: string;
}): boolean {
  return isSet(config.projectKey) && isSet(config.ingestPoint);
}

function logConfig(config: { projectKey: string; ingestPoint: string }): void {
  const { projectKey, ingestPoint } = config;
  const configErrors: string[] = [];

  if (!projectKey) configErrors.push('projectKey is empty');
  else if (projectKey.includes('<(['))
    configErrors.push('projectKey placeholder was never replaced');
  if (!ingestPoint) configErrors.push('ingestPoint is empty');
  else if (ingestPoint.includes('<(['))
    configErrors.push('ingestPoint placeholder was never replaced');
  else if (!ingestPoint.startsWith('https://'))
    configErrors.push('ingestPoint is not an https url');

  console.log(TAG, 'init', {
    origin: window.location.origin,
    secure: window.location.protocol === 'https:',
    projectKey: shortKey(projectKey),
    ingestPoint: ingestPoint || '(empty)',
    configErrors,
  });

  if (configErrors.length > 0) {
    console.error(TAG, 'CONFIG INVALID:', configErrors);
  }
}

export async function initTracker(config: {
  projectKey: string;
  ingestPoint: string;
  userId?: string;
}): Promise<void> {
  if (!isConfigured(config)) {
    console.log(TAG, 'not started: projectKey or ingestPoint is unset');
    return;
  }
  logConfig(config);

  try {
    tracker = new Tracker({
      projectKey: config.projectKey,
      ingestPoint: config.ingestPoint,
    });

    if (!(window as unknown as { __OPENREPLAY__?: unknown }).__OPENREPLAY__) {
      console.error(
        TAG,
        'constructor bailed out - duplicate instance, page not https, or browser api missing',
      );
    }

    if (config.userId) {
      tracker.setUserID(config.userId);
    }

    reportStart(await tracker.start());
  } catch (error) {
    console.error('Failed to initialize OpenReplay tracker:', error);
  }
}

export function identifyUser(
  userId: string,
  metadata?: Record<string, string>,
): void {
  const instance = tracker;
  if (!instance) {
    console.warn(TAG, 'identifyUser skipped - tracker not initialized');
    return;
  }

  instance.setUserID(userId);
  if (metadata) {
    Object.entries(metadata).forEach(([key, value]) => {
      instance.setMetadata(key, String(value));
    });
  }
  console.log(TAG, 'user identified', { userId: shortKey(userId) });
}

export function trackEvent(
  eventName: string,
  payload?: Record<string, unknown>,
): void {
  const instance = tracker;
  if (!instance) {
    console.warn(TAG, 'trackEvent skipped - tracker not initialized', {
      eventName,
      payload,
    });
    return;
  }
  const sessionId = instance.getSessionID?.() ?? null;
  console.log(TAG, 'event trigger:', eventName, {
    payload,
    sessionId,
  });
  try {
    instance.event(eventName, payload);
    console.log(TAG, 'event sent:', eventName, { sessionId });
  } catch (error) {
    console.error(TAG, 'event FAILED:', eventName, {
      payload,
      sessionId,
      error,
    });
  }
}

export function resetSession(): void {
  const instance = tracker;
  if (!instance) {
    console.warn(TAG, 'resetSession skipped - tracker not initialized');
    return;
  }

  try {
    instance.stop();
    instance.start();
    console.log(TAG, 'resetSession: restart requested');
  } catch (error) {
    console.error('Failed to reset OpenReplay session:', error);
  }
}

export function stopTracking(): void {
  if (!tracker) return;
  tracker.stop();
}

export function isTracking(): boolean {
  return tracker !== null;
}

export function getOpenReplaySessionId(): string | null {
  if (!tracker) return null;
  return tracker.getSessionID() || null;
}
